import type { ShipmentLineItem } from "~/shared/schemas/shipment-management-schema.ts";
import type { UnitType } from "~/shared/schemas/unit-type-schema.ts";
import type { HandlingUnit, Unit } from "~/shared/types/quote-details-types";
import { idGenerator } from "~/shared/utils/id-generator.ts";

const toNumberOrNull = (value?: string | null): number | null => {
	if (value == null || value.trim() === "") {
		return null;
	}
	const parsed = Number(value);
	return Number.isNaN(parsed) ? null : parsed;
};

// The shipment only stores a unit code/abbreviation (e.g. "PLT"), so resolve
// the full reference-data entry (e.g. "Pallet") to seed the dropdown correctly.
const resolveUnit = (unitCode: string | null, unitTypes: UnitType[]): Unit => {
	const match = unitTypes.find(
		(type) => type.code.toUpperCase() === (unitCode ?? "").toUpperCase(),
	);
	return (
		match ?? { id: unitCode ?? "", name: unitCode ?? "", code: unitCode ?? "" }
	);
};

/**
 * The shipment line item stores handling-unit data as loosely-typed strings
 * (quantity, unit code, dimensions, class) that differ from the structured
 * `HandlingUnit` shape the shared commodity editor expects, so map it
 * field-by-field. The unit only carries a code on the shipment, so it's
 * resolved against `unitTypes` reference data (falling back to the raw code if
 * no match is found); class only carries a code/name and is seeded from that
 * string directly.
 */
export const mapLineItemToHandlingUnit = (
	item: ShipmentLineItem,
	unitTypes: UnitType[] = [],
): HandlingUnit => ({
	id: idGenerator(),
	name: item.commodityDescription ?? "",
	description: item.commodityDescription ?? "",
	quantity: toNumberOrNull(item.handlingUnit),
	unit: resolveUnit(item.unit, unitTypes),
	weight: toNumberOrNull(item.totalWeight),
	density: item.density ?? 0,
	dimensionLength: null,
	dimensionWidth: null,
	dimensionHeight: null,
	class: {
		id: item.class ?? "",
		name: item.class ?? "",
		code: item.class ?? "",
	},
	nmfc: item.nmfc ?? "",
	isHazmat: item.hazmat ?? false,
	isStackable: item.stackable ?? false,
	pieceCount: toNumberOrNull(item.pieceCount),
	width: toNumberOrNull(item.width),
	height: toNumberOrNull(item.height),
	length: toNumberOrNull(item.length),
	estClass: toNumberOrNull(item.estimatedClass) ?? 0,
	unNumber: item.hazmatIdentificationNumber ?? null,
	properShippingName: item.hazmatProperShippingName ?? undefined,
	hazmatClass: item.hazmatHazardClass
		? { id: 0, name: item.hazmatHazardClass, code: item.hazmatHazardClass }
		: undefined,
	packingGroupId: item.hazmatPackingGroup
		? {
				id: 0,
				name: item.hazmatPackingGroup,
				code: item.hazmatPackingGroup,
				displayCode:
					item.hazmatPackingGroup === "None" ? "N/A" : item.hazmatPackingGroup,
			}
		: undefined,
});
